/**
 * Add Lift – exercise, weight, reps, video (upload or record). Submit via global upload progress.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { useUpload } from '../upload/UploadContext';
import { VideoRecorder } from '../../shared/ui/VideoRecorder';
import Display from '../../shared/ui/Display.jsx';
import Body from '../../shared/ui/Body.jsx';
import Button from '../../shared/ui/Button.jsx';
import NumericInput from '../../shared/ui/NumericInput.jsx';
import Select from '../../shared/ui/Select.jsx';
import Toggle from '../../shared/ui/Toggle.jsx';
import styles from './AddLiftPage.module.css';

const EXERCISES = [
  'Back Squat', 'Front Squat', 'Bench Press', 'Deadlift', 'Overhead Press',
  'Clean', 'Snatch', 'Barbell Row', 'Other',
];

export function AddLiftPage() {
  const { userId } = useAuth();
  const navigate = useNavigate();
  const { startUpload } = useUpload();
  const [exercise, setExercise] = useState('Back Squat');
  const [weight, setWeight] = useState('');
  const [reps, setReps] = useState('1');
  const [videoFile, setVideoFile] = useState(null);
  const [videoMode, setVideoMode] = useState(null); // null | 'upload' | 'record'
  const [showInFeed, setShowInFeed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    const w = parseFloat(weight);
    if (!Number.isFinite(w) || w <= 0) {
      setError('Enter a valid weight.');
      return;
    }
    if (!videoFile) {
      setError('Video proof is required. Upload or record a video of your lift.');
      return;
    }
    setLoading(true);
    try {
      const formData = {
        exercise,
        weight: w,
        reps: parseInt(reps, 10) || 1,
        showInFeed,
      };
      await startUpload(formData, videoFile);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err.message || 'Submit failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className={styles.page}>
      <Display as="h1" size="l" style={{ marginBottom: '0.75rem' }}>Add Lift</Display>

      <div className={styles.verificationHint}>
        <Body tone="secondary" as="span">
          <strong className={styles.hintStrong}>How verification works:</strong>{' '}
          Your lift can appear on the feed. Other athletes can tap <strong className={styles.hintStrong}>Verify</strong>.
          When your lift gets <strong className={styles.hintStrong}>20 verifications</strong>, it becomes verified.
        </Body>
      </div>

      <form onSubmit={handleSubmit} className={styles.form}>

        {/* Hero: Weight */}
        <NumericInput
          label="WEIGHT"
          unit="kg"
          step="0.5"
          min="0"
          placeholder="e.g. 120"
          value={weight}
          onChange={(e) => setWeight(e.target.value)}
          className={styles.heroInput}
          required
        />

        {/* Reps */}
        <NumericInput
          label="REPS"
          min="1"
          value={reps}
          onChange={(e) => setReps(e.target.value)}
        />

        {/* Lift type */}
        <Select
          label="LIFT"
          options={EXERCISES}
          value={exercise}
          onChange={(e) => setExercise(e.target.value)}
          required
        />

        {/* Video proof */}
        <div className={styles.videoSection}>
          <Body tone="secondary" as="span" style={{ fontSize: 13, letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 500 }}>
            Video proof
          </Body>
          <div className={styles.videoModeTabs}>
            <button
              type="button"
              className={videoMode === 'upload' ? styles.tabActive : styles.tab}
              onClick={() => { setVideoMode('upload'); setVideoFile(null); }}
            >
              Upload
            </button>
            <button
              type="button"
              className={videoMode === 'record' ? styles.tabActive : styles.tab}
              onClick={() => { setVideoMode('record'); setVideoFile(null); }}
            >
              Record
            </button>
          </div>

          {videoMode === 'upload' && (
            <>
              <input
                type="file"
                accept="video/*"
                onChange={(e) => setVideoFile(e.target.files?.[0] || null)}
                className={styles.fileInput}
              />
              {videoFile && (
                <div className={styles.videoChosen}>
                  <Body tone="secondary" as="span">{videoFile.name}</Body>
                  <button type="button" className={styles.clearVideo} onClick={() => setVideoFile(null)}>Clear</button>
                </div>
              )}
            </>
          )}

          {videoMode === 'record' && (
            <VideoRecorder
              onRecorded={(file) => setVideoFile(file)}
              onError={(msg) => setError(msg)}
            />
          )}
        </div>

        {/* Show in feed */}
        <Toggle
          label="Show this lift in the feed so others can verify it"
          checked={showInFeed}
          onChange={(e) => setShowInFeed(e.target.checked)}
        />

        {error && (
          <Body tone="secondary" as="p" style={{ color: 'var(--red-platform)', fontSize: 14 }}>
            {error}
          </Body>
        )}

        <div className={styles.actions}>
          <Button
            variant="primary"
            size="lg"
            type="submit"
            disabled={loading || !videoFile}
          >
            {loading ? 'Submitting…' : 'Add lift'}
          </Button>
          <Button
            variant="ghost"
            size="lg"
            type="button"
            onClick={() => navigate(-1)}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}
